import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { safeFetch } from '../utils/requestUtils';
import { getSession } from '../api/supabase';

/**
 * React Query hooks for API Keys management
 */

// Helper to get auth headers - async to fetch session
const getAuthHeaders = async () => {
  const session = await getSession();
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Fetch all API keys for current user
export function useApiKeys() {
  return useQuery({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch('/api-keys', {
        method: 'GET',
        headers: authHeaders
      });
      return response.api_keys || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2
  });
}

// Create a new API key
export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyData) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch('/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(keyData)
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch API keys list
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    }
  });
}

// Revoke an API key
export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyId) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch API keys list
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    }
  });
}

// Update an API key (name, scopes, expiration)
export function useUpdateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ keyId, updates }) => {
      const authHeaders = await getAuthHeaders();
      const response = await safeFetch(`/api-keys/${keyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(updates)
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
    }
  });
}
